import {
	Alert,
	Autocomplete,
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	TextField,
} from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getSmsTypesRequest, sendSmsRequest } from 'redux/reducers/sms/reducer';
import {
	getSmsSendLoading,
	getSmsSendSuccess,
	getSmsTypesData,
	getSmsTypesLoading,
} from 'redux/reducers/sms/selectors';
import { TSmsTypeItem } from 'redux/reducers/sms/types';
import { TSendSmsDialogProps } from './types';

export const SendSmsDialog = ({ open, onClose, emId, phone }: TSendSmsDialogProps) => {
	const dispatch = useDispatch();
	const smsTypes = useSelector(getSmsTypesData);
	const smsTypesLoading = useSelector(getSmsTypesLoading);
	const smsSendLoading = useSelector(getSmsSendLoading);
	const smsSendSuccess = useSelector(getSmsSendSuccess);
	const [typeHandle, setTypeHandle] = useState<string | null>(null);
	const selectedType = useMemo(
		() => smsTypes.find((type) => type.handle === typeHandle) ?? null,
		[smsTypes, typeHandle],
	);
	const normalizedPhone = phone?.trim() ?? '';

	const handleClose = () => {
		if (smsSendLoading) return;
		setTypeHandle(null);
		onClose();
	};

	const handleSend = () => {
		if (!typeHandle || !normalizedPhone) return;

		dispatch(
			sendSmsRequest({
				emId,
				// body: null,
				typeHandle,
				// params: null,
				recipient: normalizedPhone,
				// relatedEntityId: null,
			}),
		);
	};

	useEffect(() => {
		if (open) {
			dispatch(getSmsTypesRequest());
		}
	}, [dispatch, open]);

	const handleCloseRef = useRef(handleClose);
	useEffect(() => {
		handleCloseRef.current = handleClose;
	});

	const prevSmsSendLoadingRef = useRef(smsSendLoading);
	useEffect(() => {
		const wasLoading = prevSmsSendLoadingRef.current;
		prevSmsSendLoadingRef.current = smsSendLoading;
		if (open && wasLoading && !smsSendLoading && smsSendSuccess) {
			handleCloseRef.current();
		}
	}, [open, smsSendLoading, smsSendSuccess]);

	return (
		<Dialog
			open={open}
			onClose={smsSendLoading ? () => {} : handleClose}
			fullWidth
			maxWidth="sm"
			PaperProps={{
				sx: { borderRadius: '12px' },
			}}
		>
			<DialogTitle color="primary">Send SMS</DialogTitle>
			<DialogContent>
				<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
					{!normalizedPhone && (
						<Alert variant="filled" severity="warning">
							SMS cannot be sent because the EM phone number is missing.
						</Alert>
					)}
					<TextField label="Recipient" size="small" fullWidth value={normalizedPhone} disabled />
					<Autocomplete<TSmsTypeItem>
						options={smsTypes}
						loading={smsTypesLoading}
						disabled={smsSendLoading || !normalizedPhone}
						getOptionLabel={(option) => option.description || option.handle}
						value={selectedType}
						onChange={(_event, option) => {
							setTypeHandle(option?.handle ?? null);
						}}
						isOptionEqualToValue={(option, value) => option.id === value.id}
						renderInput={(params) => (
							<TextField
								{...params}
								label="SMS type"
								size="small"
								required
								InputProps={{
									...params.InputProps,
									endAdornment: (
										<>
											{smsTypesLoading ? (
												<CircularProgress color="inherit" size={18} sx={{ mr: 1 }} />
											) : null}
											{params.InputProps.endAdornment}
										</>
									),
								}}
							/>
						)}
					/>
				</Box>
			</DialogContent>
			<DialogActions sx={{ px: 3, pb: 2 }}>
				<Button onClick={handleClose} disabled={smsSendLoading}>
					Cancel
				</Button>
				<Button
					variant="contained"
					color="primary"
					disabled={smsSendLoading || !typeHandle || !normalizedPhone}
					onClick={handleSend}
				>
					{smsSendLoading ? <CircularProgress size={22} color="inherit" /> : 'Send'}
				</Button>
			</DialogActions>
		</Dialog>
	);
};
